import { useState, useEffect } from 'react'
import apiService from '../services/api'

const INVITE_TYPES = [
  { value: 'entrepreneur', label: 'Entrepreneur' },
  { value: 'individual', label: 'Individual' },
  { value: 'sme', label: 'SME' },
  { value: 'enterprise', label: 'Enterprise' }
]

const AdminInvites = () => {
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [creating, setCreating] = useState(false)
  const [sending, setSending] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [showEmailPreview, setShowEmailPreview] = useState(null)
  const [emailPreviewContent, setEmailPreviewContent] = useState(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [showChaseHistory, setShowChaseHistory] = useState(null)
  const [chaseLogs, setChaseLogs] = useState([])
  const [loadingChaseLogs, setLoadingChaseLogs] = useState(false)

  // Filter and sort state
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [sortBy, setSortBy] = useState('created_desc')

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    location: '',
    type: 'entrepreneur',
    expires_in: 0 // 0 = never expires
  })

  useEffect(() => {
    loadInvites()
  }, [])

  const loadInvites = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiService.listInvites()
      setInvites(data.invites || [])
    } catch (err) {
      console.error('Error loading invites:', err)
      setError(err.message || 'Failed to load invites')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateInvite = async (e) => {
    e.preventDefault()
    if (!formData.name || !formData.email) {
      setError('Name and email are required')
      return
    }

    try {
      setCreating(true)
      setError(null)
      await apiService.generateInvite({
        count: 1,
        expires_in: formData.expires_in,
        name: formData.name,
        email: formData.email,
        location: formData.location,
        type: formData.type
      })
      // Reset form and reload
      setFormData({
        name: '',
        email: '',
        location: '',
        type: 'entrepreneur',
        expires_in: 0
      })
      await loadInvites()
    } catch (err) {
      console.error('Error creating invite:', err)
      setError(err.message || 'Failed to create invite')
    } finally {
      setCreating(false)
    }
  }

  const handleSendEmail = async (inviteCode) => {
    try {
      setSending(inviteCode)
      setError(null)
      await apiService.sendInviteEmail(inviteCode)
      await loadInvites()
      setShowEmailPreview(null)
    } catch (err) {
      console.error('Error sending invite email:', err)
      setError(err.message || 'Failed to send email')
    } finally {
      setSending(null)
    }
  }

  const handleDeleteInvite = async (inviteCode) => {
    try {
      setDeleting(inviteCode)
      setError(null)
      await apiService.deleteInvite(inviteCode)
      await loadInvites()
      setConfirmDelete(null)
    } catch (err) {
      console.error('Error deleting invite:', err)
      setError(err.message || 'Failed to delete invite')
    } finally {
      setDeleting(null)
    }
  }

  const handleOpenEmailPreview = async (invite) => {
    setShowEmailPreview(invite)
    setEmailPreviewContent(null)
    setLoadingPreview(true)
    try {
      const preview = await apiService.previewInviteEmail(invite.code)
      setEmailPreviewContent(preview)
    } catch (err) {
      console.error('Error loading email preview:', err)
      setError(err.message || 'Failed to load email preview')
      setShowEmailPreview(null)
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleCloseEmailPreview = () => {
    setShowEmailPreview(null)
    setEmailPreviewContent(null)
  }

  const handleViewChaseHistory = async (invite) => {
    setShowChaseHistory(invite)
    setChaseLogs([])
    setLoadingChaseLogs(true)
    try {
      const data = await apiService.listChaseLogs(invite.code)
      setChaseLogs(data.logs || [])
    } catch (err) {
      console.error('Error loading chase logs:', err)
      setError(err.message || 'Failed to load chase history')
    } finally {
      setLoadingChaseLogs(false)
    }
  }

  const handleCloseChaseHistory = () => {
    setShowChaseHistory(null)
    setChaseLogs([])
  }

  const formatDate = (dateValue) => {
    if (!dateValue) return '-'
    // Handle Unix timestamps (seconds) - if it's a number less than year 3000 in ms, treat as seconds
    let date
    if (typeof dateValue === 'number') {
      // Unix timestamp in seconds - convert to milliseconds
      date = new Date(dateValue * 1000)
    } else {
      date = new Date(dateValue)
    }
    // Check for invalid date
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (invite) => {
    if (invite.used) {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-300 border border-green-500/30 whitespace-nowrap">
          Signed Up
        </span>
      )
    }
    // Check for chase status
    if (invite.status) {
      const statusColors = {
        'Email Sent': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
        '1st Email Chaser Sent': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
        '2nd Email Chaser Sent': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
        '3rd Email Chaser Sent': 'bg-red-500/20 text-red-300 border-red-500/30',
        'Closed - No Response': 'bg-gray-500/20 text-gray-300 border-gray-500/30',
      }
      const colorClass = statusColors[invite.status] || 'bg-blue-500/20 text-blue-300 border-blue-500/30'
      return (
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${colorClass}`}>
          {invite.status}
        </span>
      )
    }
    if (invite.email_sent) {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 whitespace-nowrap">
          Email Sent
        </span>
      )
    }
    // Check if expired
    if (invite.expires_at) {
      // Handle Unix timestamp (seconds)
      const expiryMs = typeof invite.expires_at === 'number' ? invite.expires_at * 1000 : new Date(invite.expires_at).getTime()
      if (expiryMs < Date.now()) {
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-300 border border-red-500/30">
            Expired
          </span>
        )
      }
    }
    return (
      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
        Pending
      </span>
    )
  }

  const getTypeLabel = (type) => {
    const typeObj = INVITE_TYPES.find(t => t.value === type)
    return typeObj ? typeObj.label : type || '-'
  }

  // Get status string for filtering
  const getInviteStatus = (invite) => {
    if (invite.used) return 'signed_up'
    if (invite.status === 'Closed - No Response') return 'closed'
    if (invite.status === '3rd Email Chaser Sent') return 'chaser_3'
    if (invite.status === '2nd Email Chaser Sent') return 'chaser_2'
    if (invite.status === '1st Email Chaser Sent') return 'chaser_1'
    if (invite.status === 'Email Sent' || invite.email_sent) return 'email_sent'
    if (invite.expires_at) {
      const expiryMs = typeof invite.expires_at === 'number' ? invite.expires_at * 1000 : new Date(invite.expires_at).getTime()
      if (expiryMs < Date.now()) return 'expired'
    }
    return 'pending'
  }

  // Filter and sort invites
  const filteredAndSortedInvites = invites
    .filter(invite => {
      if (statusFilter !== 'all' && getInviteStatus(invite) !== statusFilter) return false
      if (typeFilter !== 'all' && invite.invite_type !== typeFilter) return false
      return true
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'created_desc':
          return (b.created_at || 0) - (a.created_at || 0)
        case 'created_asc':
          return (a.created_at || 0) - (b.created_at || 0)
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '')
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '')
        case 'email_asc':
          return (a.email || '').localeCompare(b.email || '')
        case 'email_desc':
          return (b.email || '').localeCompare(a.email || '')
        default:
          return 0
      }
    })

  // Email preview modal
  const EmailPreviewModal = ({ invite, content, isLoading, onClose, onSend }) => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Email Preview</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          ) : content ? (
            <>
              <div className="bg-gray-900 rounded-xl p-6 mb-6">
                <div className="mb-4 text-sm text-gray-400">
                  <strong>To:</strong> {content.to || invite.email}
                </div>
                <div className="mb-4 text-sm text-gray-400">
                  <strong>Subject:</strong> {content.subject}
                </div>
                <hr className="border-gray-700 my-4" />
                <div className="text-gray-300 whitespace-pre-wrap font-mono text-sm">
                  {content.body}
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => onSend(invite.code)}
                  disabled={sending === invite.code}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-500 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {sending === invite.code ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-400">
              Failed to load email preview
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <>
      {/* Create Invite Form */}
      <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Create New Invite</h3>
        <form onSubmit={handleCreateInvite} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="John Doe"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="john@example.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="London, UK"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {INVITE_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Expires In</label>
            <select
              value={formData.expires_in}
              onChange={(e) => setFormData({ ...formData, expires_in: parseInt(e.target.value) })}
              className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value={0}>Never</option>
              <option value={24}>1 day</option>
              <option value={72}>3 days</option>
              <option value={168}>7 days</option>
              <option value={336}>14 days</option>
              <option value={720}>30 days</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={creating}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {creating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Invite
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300">
          {error}
        </div>
      )}

      {/* Invites Table */}
      <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden">
        <div className="p-4 border-b border-gray-700/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">All Invites</h3>
            <button
              onClick={loadInvites}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-400">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2 py-1 rounded-lg text-sm bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="email_sent">Email Sent</option>
                <option value="chaser_1">1st Chaser Sent</option>
                <option value="chaser_2">2nd Chaser Sent</option>
                <option value="chaser_3">3rd Chaser Sent</option>
                <option value="signed_up">Signed Up</option>
                <option value="closed">Closed - No Response</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-400">Type:</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-2 py-1 rounded-lg text-sm bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All</option>
                {INVITE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-400">Sort:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-2 py-1 rounded-lg text-sm bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="created_desc">Newest First</option>
                <option value="created_asc">Oldest First</option>
                <option value="name_asc">Name A-Z</option>
                <option value="name_desc">Name Z-A</option>
                <option value="email_asc">Email A-Z</option>
                <option value="email_desc">Email Z-A</option>
              </select>
            </div>
            <span className="text-xs text-gray-500 ml-auto">
              Showing {filteredAndSortedInvites.length} of {invites.length}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : filteredAndSortedInvites.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-300 mb-2">
              {invites.length === 0 ? 'No invites yet' : 'No matching invites'}
            </h3>
            <p className="text-gray-500">
              {invites.length === 0 ? 'Create your first invite using the form above' : 'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider min-w-[100px]">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Expires</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {filteredAndSortedInvites.map((invite) => (
                  <tr key={invite.code} className="hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-sm text-white">{invite.name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{invite.email || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{getTypeLabel(invite.invite_type)}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{invite.location || '-'}</td>
                    <td className="px-4 py-3">{getStatusBadge(invite)}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{formatDate(invite.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{formatDate(invite.expires_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {!invite.used && (
                          <button
                            onClick={() => handleOpenEmailPreview(invite)}
                            className="px-3 py-1 rounded-lg text-xs font-medium bg-purple-600/20 text-purple-300 hover:bg-purple-600/30 transition-colors flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {invite.email_sent ? 'Resend' : 'Send Email'}
                          </button>
                        )}
                        {invite.used && (
                          <span className="text-xs text-gray-500">Used by {invite.used_by}</span>
                        )}
                        {!invite.used && (
                          <button
                            onClick={() => setConfirmDelete(invite)}
                            className="px-3 py-1 rounded-lg text-xs font-medium bg-red-600/20 text-red-300 hover:bg-red-600/30 transition-colors flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        )}
                        {(invite.email_sent || invite.status) && (
                          <button
                            onClick={() => handleViewChaseHistory(invite)}
                            className="px-3 py-1 rounded-lg text-xs font-medium bg-gray-600/20 text-gray-300 hover:bg-gray-600/30 transition-colors flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            History
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Email Preview Modal */}
      {showEmailPreview && (
        <EmailPreviewModal
          invite={showEmailPreview}
          content={emailPreviewContent}
          isLoading={loadingPreview}
          onClose={handleCloseEmailPreview}
          onSend={handleSendEmail}
        />
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Delete Invite</h3>
                  <p className="text-sm text-gray-400">{confirmDelete.email}</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete this invite for <strong>{confirmDelete.name || confirmDelete.email}</strong>? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteInvite(confirmDelete.code)}
                  disabled={deleting === confirmDelete.code}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-500 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {deleting === confirmDelete.code ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Invite
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chase History Modal */}
      {showChaseHistory && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">Chase Email History</h3>
                  <p className="text-sm text-gray-400">{showChaseHistory.name || showChaseHistory.email}</p>
                </div>
                <button onClick={handleCloseChaseHistory} className="text-gray-400 hover:text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingChaseLogs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
              ) : chaseLogs.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-400">No chase emails sent yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {chaseLogs.map((log) => (
                    <div key={log.id} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {log.success ? (
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          ) : (
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                          )}
                          <span className="text-sm font-medium text-white">{log.chase_type}</span>
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(log.sent_at)}</span>
                      </div>
                      <div className="mt-2 text-sm text-gray-400">
                        To: {log.email}
                      </div>
                      {log.error && (
                        <div className="mt-2 text-sm text-red-400">
                          Error: {log.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end">
              <button
                onClick={handleCloseChaseHistory}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AdminInvites
